import { MouseEvent, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import MainCard from '@/components/MainCard';
import Avatar from '@/components/@extended/Avatar';
import IconButton from '@/components/@extended/IconButton';
import Transitions from '@/components/@extended/Transitions';
import TruncatedText from '@/components/TruncatedText';
import { useAuthContext } from '@/providers';
import { useGetMenuMaster } from '@/api/menu';
import { IAppUser } from '@/hooks';
import { avatarUrlOf } from '@/hooks/fetch-hooks/users-api';

// assets
import UpOutlined from '@ant-design/icons/UpOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import VersionInfo from './VersionInfo';
import PopperStyled from './Navigation/MiniPopperStyled';

// ==============================|| DRAWER - USER + ACCOUNT ||============================== //

interface UserAction {
	label: string;
	icon: ReactNode;
	onClick: () => void;
	danger?: boolean;
}

type AccountAdministration = {
	id?: number;
	name?: string;
	level?: { id?: number; name?: string };
	// api/users/me populează administrațiile cu locațiile detaliate (județ/UAT) → nu mai
	// e nevoie de un GET api/administrations/:id separat (care oricum dă 403 fără drept).
	locations?: Array<{
		county?: { id?: number; name?: string } | null;
		uat?: { id?: number; name?: string } | null;
	}>;
};

// Niveluri administrație (api/administration-levels): 1 local, 2 județean, 3 național.
const NATIONAL_LEVEL_ID = 3;

const uniqueNames = (values: Array<string | undefined>): string[] =>
	Array.from(new Set(values.filter((v): v is string => Boolean(v))));

function Row({ label, value }: { label: string; value: ReactNode }) {
	return (
		<Stack
			direction="row"
			sx={{ gap: 1, justifyContent: 'space-between', alignItems: 'center' }}
		>
			<Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
				{label}
			</Typography>
			{typeof value === 'string' ? (
				<Typography
					variant="caption"
					sx={{
						fontWeight: 500,
						textAlign: 'right',
						minWidth: 0,
						wordBreak: 'break-word'
					}}
				>
					{value}
				</Typography>
			) : (
				value
			)}
		</Stack>
	);
}

// Valoare rezumat trunchiată cu „…" + tooltip cu detaliul complet (pentru liste lungi de localități).
function SummaryValue({ text, tooltip }: { text: string; tooltip?: ReactNode }) {
	return (
		<Tooltip title={tooltip ?? text}>
			<Typography
				variant="caption"
				noWrap
				sx={{ fontWeight: 500, textAlign: 'right', flex: 1, minWidth: 0 }}
			>
				{text}
			</Typography>
		</Tooltip>
	);
}

// Un bloc = o administrație cu detaliile ei (Nume + Nivel + Județ + UAT), ca să nu se suprapună câmpurile.
function AdminBlock({
	name,
	levelName,
	levelId,
	counties,
	uats,
	allLocalities
}: {
	name: string;
	levelName?: string;
	levelId?: number;
	counties: string[];
	uats: string[];
	allLocalities?: boolean;
}) {
	const isNational = levelId === NATIONAL_LEVEL_ID;
	// Județ: unul singur → nume; mai multe → „N județe" (+ tooltip listă).
	const countyText =
		counties.length === 0
			? 'Nu este specificat'
			: counties.length === 1
				? counties[0]
				: `${counties.length} județe`;
	const countyTooltip = counties.length > 1 ? counties.join(', ') : undefined;

	// Localități: județ integral → „Toate localitățile"; una → nume; mai multe → „N localități" (+ tooltip).
	let localityText = 'Nu este specificat';
	let localityTooltip: ReactNode;
	if (allLocalities && uats.length === 0) {
		localityText = 'Toate localitățile';
	} else if (uats.length === 1) {
		[localityText] = uats;
	} else if (uats.length > 1) {
		localityText = `${uats.length} localități`;
		localityTooltip = uats.join(', ');
	}

	return (
		<Stack
			sx={{
				gap: 0.75,
				p: 1.25,
				borderRadius: 1.5,
				border: '1px solid',
				borderColor: 'divider',
				bgcolor: 'background.default'
			}}
		>
			<TruncatedText variant="subtitle2">{name}</TruncatedText>
			<Row
				label="Nivel"
				value={
					levelName ? (
						<Chip size="small" variant="light" color="primary" label={levelName} />
					) : (
						'Nu este specificat'
					)
				}
			/>
			<Row
				label={counties.length > 1 ? 'Județe' : 'Județ'}
				value={<SummaryValue text={countyText} tooltip={countyTooltip} />}
			/>
			{!isNational && (
				<Row
					label={uats.length > 1 ? 'UAT-uri' : 'UAT'}
					value={<SummaryValue text={localityText} tooltip={localityTooltip} />}
				/>
			)}
		</Stack>
	);
}

type AdminBlockData = {
	key: string;
	name: string;
	levelName?: string;
	levelId?: number;
	counties: string[];
	uats: string[];
	allLocalities?: boolean;
};

// Secțiunea „Cont curent / Administrații" (blocuri complete + chip „+N") — aceeași și în
// cardul extins, și în popup-ul meniului restrâns, ca informațiile să fie identice.
function AccountAdministrationsSection({
	adminBlocks,
	extraAdministrations
}: {
	adminBlocks: AdminBlockData[];
	extraAdministrations: AccountAdministration[];
}) {
	return (
		<Stack sx={{ gap: 1 }}>
			<Typography variant="caption" sx={{ color: 'text.secondary' }}>
				{adminBlocks.length > 1 ? 'Administrații' : 'Cont curent'}
			</Typography>
			{adminBlocks.length === 0 ? (
				<AdminBlock
					name={'Nu există date disponibile'}
					levelName={'N/A'}
					counties={['N/A']}
					uats={['N/A']}
				/>
			) : (
				<Stack sx={{ gap: 1 }}>
					{adminBlocks.map((block) => (
						<AdminBlock
							key={block.key}
							name={block.name}
							levelName={block.levelName}
							levelId={block.levelId}
							counties={block.counties}
							uats={block.uats}
							allLocalities={block.allLocalities}
						/>
					))}
					{extraAdministrations.length > 0 && (
						<Tooltip
							title={extraAdministrations.map((a) => (
								<div key={a.id ?? a.name}>{a.name}</div>
							))}
						>
							<Chip
								size="small"
								variant="light"
								color="primary"
								label={`+${extraAdministrations.length} ${
									extraAdministrations.length === 1 ? 'administrație' : 'administrații'
								}`}
								sx={{ alignSelf: 'flex-start' }}
							/>
						</Tooltip>
					)}
				</Stack>
			)}
		</Stack>
	);
}

export default function NavUser() {
	const navigate = useNavigate();
	const { menuMaster } = useGetMenuMaster();
	const drawerOpen = menuMaster.isDashboardDrawerOpened;
	const { user, signOut } = useAuthContext();
	// `user` conține deja datele complete din api/users/me (status, roluri, administrații).
	const me = user as unknown as IAppUser | undefined;

	const [open, setOpen] = useState(false);
	// Flyout-ul contului din meniul restrâns — ACELAȘI pattern ca itemele de navigare
	// (MiniPopperStyled), deschis pe hover/click pe avatar.
	const [flyoutAnchor, setFlyoutAnchor] = useState<null | HTMLElement>(null);

	const handleLogout = async () => {
		try {
			await signOut();
			navigate('/auth', { replace: true });
		} catch (err) {
			console.error(err);
		}
	};

	const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '—';
	const userAvatar = avatarUrlOf(user?.avatar);
	const initial = (userName || user?.email || '?').charAt(0).toUpperCase();
	// afișare: preferăm eticheta localizată (label), fallback pe name / rolul mapat
	const roleName = me?.roles?.[0]?.label || me?.roles?.[0]?.name || user?.role || '';

	// Administrațiile din users/me (au doar id + nume). Moderatorii pot avea mai multe.
	const allAdministrations: AccountAdministration[] = me?.administrations?.length
		? (me.administrations as AccountAdministration[])
		: (me as IAppUser & { administration?: AccountAdministration })?.administration
			? [
					(me as IAppUser & { administration?: AccountAdministration })
						.administration as AccountAdministration
				]
			: [];
	// Afișăm primele 2 administrații ca blocuri complete; restul intră în tooltip „+N".
	const shownAdministrations = allAdministrations.slice(0, 2);
	const extraAdministrations = allAdministrations.slice(2);

	// Nivelul + locațiile (județ/UAT) vin DEJA din api/users/me (administrations populate cu
	// level + locations) → fără GET api/administrations/:id per bloc (evită 403 la userii
	// fără drept de citire pe administrații).
	const adminBlocks = shownAdministrations.map((admin, index) => {
		const locs = admin.locations ?? [];
		return {
			key: `${admin.id ?? index}`,
			name: admin.name ?? 'Nu este specificat',
			levelName: admin.level?.name,
			levelId: admin.level?.id,
			counties: uniqueNames(locs.map((l) => l.county?.name)),
			uats: uniqueNames(locs.map((l) => l.uat?.name)),
			// județ trimis integral (uat null) → „Toate localitățile".
			allLocalities: locs.some((l) => l.county && !l.uat)
		};
	});

	const actions: UserAction[] = [
		{
			label: 'Profil',
			icon: <UserOutlined />,
			onClick: () => navigate('/common/profile')
		},
		{
			label: 'Deconectare',
			icon: <LogoutOutlined />,
			onClick: handleLogout,
			danger: true
		}
	];

	// collapsed (mini) drawer — versiunile rămân pe indicatorul de sus; contul deschide
	// pe hover/click ACELAȘI flyout ca itemele de meniu (MiniPopperStyled) cu acțiunile
	// Profil / Deconectare — fără tooltip care să dubleze rolul.
	if (!drawerOpen) {
		return (
			<Box
				sx={{
					borderTop: '2px solid',
					borderTopColor: 'divider',
					// Ritm vertical uniform: divider → punct versiuni → avatar, distanțe egale;
					// jos, aer mai mult (36px) ca blocul să nu stea lipit de marginea ecranului.
					pt: 1.25,
					pb: '24px',
					px: 1,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 1
				}}
			>
				<VersionInfo mini />
				<Box
					sx={{ position: 'relative' }}
					onMouseEnter={(e: MouseEvent<HTMLElement>) => setFlyoutAnchor(e.currentTarget)}
					onMouseLeave={() => setFlyoutAnchor(null)}
				>
					<IconButton
						color="secondary"
						sx={{ p: 0.5 }}
						onClick={(e: MouseEvent<HTMLElement>) =>
							setFlyoutAnchor((prev) => (prev ? null : e.currentTarget))
						}
						aria-label="Meniu utilizator"
						aria-haspopup="true"
						aria-expanded={Boolean(flyoutAnchor)}
					>
						<Avatar alt={userName} src={userAvatar}>
							{initial}
						</Avatar>
					</IconButton>
					<PopperStyled
						open={Boolean(flyoutAnchor)}
						anchorEl={flyoutAnchor}
						// contul e în colțul din stânga-jos → flyout-ul se deschide mereu în sus
						// (right-end), aliniat cu baza avatarului
						placement="right-end"
						style={{ zIndex: 2001 }}
						sx={{
							// reglajele variantei de meniu (-8px) nu se aplică aici: baza panoului
							// stă exact pe baza avatarului, iar săgeata pe CENTRUL lui
							'&[data-popper-placement="right-end"] .MuiPaper-root': {
								marginBottom: 0
							},
							'& > .MuiBox-root::before': { top: 'auto', bottom: 19 }
						}}
						popperOptions={{
							modifiers: [
								{ name: 'offset', options: { offset: [16, 16] } },
								{
									name: 'preventOverflow',
									options: { boundary: 'viewport', padding: 12, tether: false }
								}
							]
						}}
					>
						{({ TransitionProps }) => (
							<Transitions in={Boolean(flyoutAnchor)} {...TransitionProps}>
								<Paper
									sx={(theme) => ({
										overflow: 'hidden',
										py: 0.5,
										boxShadow: theme.vars.customShadows.z1,
										backgroundImage: 'none',
										border: '1px solid',
										borderColor: 'divider'
									})}
								>
									{actions.map((a) => (
										<ListItemButton
											key={a.label}
											onClick={() => {
												setFlyoutAnchor(null);
												a.onClick();
											}}
											sx={(theme) => ({
												mx: 0.75,
												my: 0.25,
												borderRadius: 1.5,
												'&:hover': {
													bgcolor: 'primary.lighter',
													...theme.applyStyles('dark', { bgcolor: 'divider' })
												},
												...(a.danger && { color: 'error.main' })
											})}
										>
											<ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
												{a.icon}
											</ListItemIcon>
											<ListItemText
												primary={
													<Typography variant="h6" sx={{ color: 'inherit' }}>
														{a.label}
													</Typography>
												}
											/>
										</ListItemButton>
									))}
								</Paper>
							</Transitions>
						)}
					</PopperStyled>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ borderTop: '2px solid', borderTopColor: 'divider' }}>
			<Box sx={{ p: 2 }}>
				<VersionInfo />
				<MainCard content={false} sx={{ p: 1.5 }}>
					{/* user identity — header */}
					<Stack
						direction="row"
						onClick={() => setOpen((o) => !o)}
						sx={{ gap: 1.25, alignItems: 'center', cursor: 'pointer' }}
					>
						<Avatar alt={userName} src={userAvatar} sx={{ width: 40, height: 40 }}>
							{initial}
						</Avatar>
						<Stack sx={{ flex: 1, minWidth: 0 }}>
							<TruncatedText variant="subtitle1">{userName}</TruncatedText>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>
								{roleName}
							</Typography>
						</Stack>
						<IconButton
							size="small"
							color="secondary"
							aria-label={open ? 'Restrânge' : 'Extinde'}
						>
							{open ? <UpOutlined /> : <DownOutlined />}
						</IconButton>
					</Stack>

					<Collapse in={open}>
						<Divider sx={{ my: 1.5 }} />
						<AccountAdministrationsSection
							adminBlocks={adminBlocks}
							extraAdministrations={extraAdministrations}
						/>

						{/* actions */}
						<Divider sx={{ my: 1.5 }} />
						<List disablePadding sx={{ '& .MuiListItemIcon-root': { minWidth: 30 } }}>
							{actions.map((a) => (
								<ListItemButton
									key={a.label}
									onClick={() => {
										setOpen(false);
										a.onClick();
									}}
									sx={{ borderRadius: 1, ...(a.danger && { color: 'error.main' }) }}
								>
									<ListItemIcon sx={{ ...(a.danger && { color: 'error.main' }) }}>
										{a.icon}
									</ListItemIcon>
									<ListItemText primary={a.label} />
								</ListItemButton>
							))}
						</List>
					</Collapse>
				</MainCard>
			</Box>
		</Box>
	);
}
